import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message, 
  Space, 
  Tag, 
  Popconfirm,
  Row,
  Col,
  Typography,
  Divider
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  StopOutlined, 
  PlayCircleOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  IdcardOutlined
} from '@ant-design/icons';
import employeeService from '../../firebase/employeeService';

const { Title, Text } = Typography;
const { Option } = Select;

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    showQuickJumper: true,
    pageSizeOptions: ['10', '20', '50', '100'],
  });

  // Load employees on component mount
  useEffect(() => {
    loadEmployees();
  }, []);

  /**
   * Load all employees from the backend
   */
  const loadEmployees = async () => {
    setLoading(true);
    try {
      const result = await employeeService.getEmployees();
      if (result.success) {
        setEmployees(result.employees);
      } else {
        message.error(result.error || 'Failed to load employees');
      }
    } catch (error) {
      console.error('Error loading employees:', error);
      message.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle creating a new employee
   */
  const handleCreateEmployee = () => {
    setEditingEmployee(null);
    form.resetFields();
    setModalVisible(true);
  };

  /**
   * Handle editing an existing employee
   */
  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    form.setFieldsValue({
      name: employee.name,
      displayName: employee.displayName,
      email: employee.email,
      employeeId: employee.employeeId,
      department: employee.department,
      position: employee.position,
      phoneNumber: employee.phoneNumber
    });
    setModalVisible(true);
  };

  /**
   * Handle form submission (create or update)
   */
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      console.log('🔍 Form submission data:', values);
      console.log('🔍 Editing employee:', editingEmployee?.uid);
      
      let result;
      
      if (editingEmployee) {
        // Update existing employee
        console.log('🔍 Calling updateEmployee with:', {
          uid: editingEmployee.uid,
          values: values
        });
        result = await employeeService.updateEmployee(editingEmployee.uid, values);
        console.log('🔍 Update result:', result);
      } else {
        // Create new employee
        console.log('🔍 Calling createEmployee with:', values);
        result = await employeeService.createEmployee(values);
        console.log('🔍 Create result:', result);
      }

      if (result.success) {
        message.success(result.message || `Employee ${editingEmployee ? 'updated' : 'created'} successfully`);
        setModalVisible(false);
        form.resetFields();
        console.log('🔍 Reloading employees list...');
        loadEmployees(); // Reload the list
      } else {
        console.error('🔍 Operation failed:', result.error);
        message.error(result.error || `Failed to ${editingEmployee ? 'update' : 'create'} employee`);
      }
    } catch (error) {
      console.error('Error submitting employee:', error);
      message.error(`Failed to ${editingEmployee ? 'update' : 'create'} employee`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle deactivating an employee
   */
  const handleDeactivateEmployee = async (employee) => {
    setLoading(true);
    try {
      const result = await employeeService.deactivateEmployee(employee.uid);
      if (result.success) {
        message.success(result.message || 'Employee deactivated successfully');
        loadEmployees(); // Reload the list
      } else {
        message.error(result.error || 'Failed to deactivate employee');
      }
    } catch (error) {
      console.error('Error deactivating employee:', error);
      message.error('Failed to deactivate employee');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle reactivating an employee
   */
  const handleReactivateEmployee = async (employee) => {
    setLoading(true);
    try {
      const result = await employeeService.reactivateEmployee(employee.uid);
      if (result.success) {
        message.success(result.message || 'Employee reactivated successfully');
        loadEmployees(); // Reload the list
      } else {
        message.error(result.error || 'Failed to reactivate employee');
      }
    } catch (error) {
      console.error('Error reactivating employee:', error);
      message.error('Failed to reactivate employee');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Table columns configuration
   */
  const columns = [
    {
      title: 'Employee ID',
      dataIndex: 'employeeId',
      key: 'employeeId',
      render: (text) => (
        <Space>
          <IdcardOutlined />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <UserOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: 'Display Name',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (text) => text || 'Not set',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => (
        <Space>
          <MailOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (text) => text || 'Not specified',
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      render: (text) => text || 'Not specified',
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (text) => (
        text ? (
          <Space>
            <PhoneOutlined />
            {text}
          </Space>
        ) : 'Not specified'
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => {
        if (!date) return 'N/A';
        const dateObj = date instanceof Date ? date : new Date(date);
        return dateObj.toLocaleDateString();
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditEmployee(record)}
          >
            Edit
          </Button>
          {record.isActive ? (
            <Popconfirm
              title="Are you sure you want to deactivate this employee?"
              onConfirm={() => handleDeactivateEmployee(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="default"
                size="small"
                icon={<StopOutlined />}
                danger
              >
                Deactivate
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Are you sure you want to reactivate this employee?"
              onConfirm={() => handleReactivateEmployee(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="default"
                size="small"
                icon={<PlayCircleOutlined />}
              >
                Reactivate
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              Employee Management
            </Title>
            <Text type="secondary">
              Manage employee accounts for the staff portal
            </Text>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateEmployee}
              size="large"
            >
              Add Employee
            </Button>
          </Col>
        </Row>

        <Divider />

        <Table
          columns={columns}
          dataSource={employees}
          rowKey="uid"
          loading={loading}
          pagination={{
            ...pagination,
            total: employees.length,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} employees`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: pageSize
              }));
            },
            onShowSizeChange: (current, size) => {
              setPagination(prev => ({
                ...prev,
                current: 1, // Reset to first page when changing page size
                pageSize: size
              }));
            }
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Employee Form Modal */}
      <Modal
        title={editingEmployee ? 'Edit Employee' : 'Add New Employee'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Full Name"
                rules={[
                  { required: true, message: 'Please enter employee name' },
                  { min: 2, message: 'Name must be at least 2 characters' }
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Enter full name"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="displayName"
                label="Display Name"
                rules={[
                  { max: 50, message: 'Display name must be less than 50 characters' }
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="e.g., Johnny, Mike, etc. (Optional)"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="employeeId"
                label="Employee ID"
                rules={[
                  { required: true, message: 'Please enter employee ID' },
                  { min: 3, message: 'Employee ID must be at least 3 characters' }
                ]}
              >
                <Input
                  prefix={<IdcardOutlined />}
                  placeholder="Enter employee ID"
                  disabled={!!editingEmployee} // Disable editing for existing employees
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: 'Please enter email address' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Enter email address"
                  disabled={!!editingEmployee} // Disable editing for existing employees
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phoneNumber"
                label="Phone Number"
                rules={[
                  {
                    pattern: /^[689]\d{7}$/,
                    message: 'Enter valid Singapore phone number (8 digits starting with 6, 8, or 9)'
                  }
                ]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="Enter phone number"
                  maxLength={8}
                />
              </Form.Item>
            </Col>
          </Row>

          {!editingEmployee && (
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please enter password' },
                { min: 6, message: 'Password must be at least 6 characters' }
              ]}
            >
              <Input.Password
                placeholder="Enter password"
              />
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="department"
                label="Department"
              >
                <Select placeholder="Select department" allowClear>
                  <Option value="Operations">Operations</Option>
                  <Option value="Sales">Sales</Option>
                  <Option value="Administration">Administration</Option>
                  <Option value="Logistics">Logistics</Option>
                  <Option value="Maintenance">Maintenance</Option>
                  <Option value="Safety">Safety</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="position"
                label="Position"
              >
                <Input placeholder="Enter position/job title" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                {editingEmployee ? 'Update Employee' : 'Create Employee'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmployeeManagement;
